import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Car, 
  Plus, 
  Pencil, 
  Trash2,
  FileDown
} from "lucide-react";

export default function ViaturasPage() {
  const { user } = useAuth();
  const [viaturas, setViaturas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingViatura, setEditingViatura] = useState(null);
  const [formData, setFormData] = useState({
    empresa_id: "",
    matricula: "",
    nome_condutor: "",
    contacto_condutor: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [viaturasRes, empresasRes] = await Promise.all([
        axios.get(`${API}/viaturas`),
        axios.get(`${API}/empresas`)
      ]);
      setViaturas(viaturasRes.data);
      setEmpresas(empresasRes.data);
      
      if (user?.empresa_id) {
        setFormData(prev => ({ ...prev, empresa_id: user.empresa_id }));
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        empresa_id: formData.empresa_id,
        matricula: formData.matricula.toUpperCase(),
        nome_condutor: formData.nome_condutor,
        contacto_condutor: formData.contacto_condutor || null
      };

      if (editingViatura) {
        await axios.put(`${API}/viaturas/${editingViatura.id}`, {
          nome_condutor: payload.nome_condutor,
          contacto_condutor: payload.contacto_condutor
        });
        toast.success("Viatura atualizada");
      } else {
        await axios.post(`${API}/viaturas`, payload);
        toast.success("Viatura criada");
      }
      setDialogOpen(false);
      setEditingViatura(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar viatura");
    }
  };

  const handleEdit = (viatura) => {
    setEditingViatura(viatura);
    setFormData({
      empresa_id: viatura.empresa_id,
      matricula: viatura.matricula,
      nome_condutor: viatura.nome_condutor,
      contacto_condutor: viatura.contacto_condutor || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (viaturaId) => {
    if (!confirm("Tem a certeza que deseja eliminar esta viatura?")) return;
    try {
      await axios.delete(`${API}/viaturas/${viaturaId}`);
      toast.success("Viatura eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar viatura");
    }
  };

  const downloadPdf = async (viaturaId) => {
    try {
      const response = await axios.get(`${API}/viaturas/${viaturaId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `viatura_${viaturaId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descarregado");
    } catch (error) {
      toast.error("Erro ao descarregar PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      empresa_id: user?.empresa_id || "",
      matricula: "",
      nome_condutor: "",
      contacto_condutor: ""
    });
  };

  const getEmpresaName = (empresaId) => empresas.find(e => e.id === empresaId)?.nome_empresa || "N/A";

  const headerAction = (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) {
        setEditingViatura(null);
        resetForm();
      }
    }}>
      <DialogTrigger asChild>
        <Button className="bg-[#0047FF] hover:bg-[#0035CC]" data-testid="add-viatura-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Viatura
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingViatura ? "Editar Viatura" : "Nova Viatura"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Empresa</Label>
            <Select 
              value={formData.empresa_id} 
              onValueChange={(value) => setFormData({ ...formData, empresa_id: value })}
              disabled={!!editingViatura || !!user?.empresa_id}
            >
              <SelectTrigger data-testid="viatura-empresa-select">
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                {empresas.map((empresa) => (
                  <SelectItem key={empresa.id} value={empresa.id}>{empresa.nome_empresa}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="matricula">Matrícula</Label>
            <Input
              id="matricula"
              value={formData.matricula}
              onChange={(e) => setFormData({ ...formData, matricula: e.target.value.toUpperCase() })}
              placeholder="Ex: AA-00-BB"
              required
              disabled={!!editingViatura}
              data-testid="viatura-matricula-input"
              className="uppercase"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="condutor">Nome do Condutor</Label>
            <Input
              id="condutor"
              value={formData.nome_condutor}
              onChange={(e) => setFormData({ ...formData, nome_condutor: e.target.value })}
              placeholder="Ex: João Silva"
              required
              data-testid="viatura-condutor-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contacto">Contacto (opcional)</Label>
            <Input
              id="contacto"
              value={formData.contacto_condutor}
              onChange={(e) => setFormData({ ...formData, contacto_condutor: e.target.value })}
              placeholder="Ex: 912345678"
              data-testid="viatura-contacto-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-[#0047FF] hover:bg-[#0035CC]" data-testid="viatura-submit-btn">
              {editingViatura ? "Guardar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Viaturas" subtitle="Gestão de viaturas e QR Codes" headerAction={headerAction}>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0047FF]"></div>
        </div>
      ) : viaturas.length === 0 ? (
        <Card className="border-[#E2E8F0]">
          <CardContent className="p-8 text-center">
            <Car className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
            <p className="text-[#64748B] mb-4">Nenhuma viatura registada</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#0047FF] hover:bg-[#0035CC]">
              <Plus className="w-4 h-4 mr-2" />
              Registar Primeira Viatura
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Condutor</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {viaturas.map((viatura) => (
                  <TableRow key={viatura.id} data-testid={`viatura-row-${viatura.id}`}>
                    <TableCell className="font-bold tracking-wider">{viatura.matricula}</TableCell>
                    <TableCell>{getEmpresaName(viatura.empresa_id)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{viatura.nome_condutor}</p>
                        {viatura.contacto_condutor && (
                          <p className="text-sm text-[#64748B]">{viatura.contacto_condutor}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant={viatura.estado_acesso === "dentro" ? "default" : "secondary"}
                        className={viatura.estado_acesso === "dentro" ? "bg-[#00C853]" : ""}
                      >
                        {viatura.estado_acesso === "dentro" ? "Dentro" : "Fora"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => downloadPdf(viatura.id)}
                          title="Descarregar PDF"
                          data-testid={`download-pdf-${viatura.id}`}
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(viatura)} data-testid={`edit-viatura-${viatura.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-[#D50000]" onClick={() => handleDelete(viatura.id)} data-testid={`delete-viatura-${viatura.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </PageLayout>
  );
}
